import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../ui/dialog';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../ui/accordion';
import { Switch } from '../ui/switch';
import {
  Plus, Upload, Trash2, FileText, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';

export const InterviewManagement = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newInterviewName, setNewInterviewName] = useState('');
  const [candidates, setCandidates] = useState({});

  useEffect(() => {
    fetchInterviews();
  }, []);

  const fetchInterviews = async () => {
    try {
      const response = await api.get('/admin/interviews');
      setInterviews(response.data);
    } catch (error) {
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async (interviewId) => {
    try {
      const response = await api.get(`/admin/interviews/${interviewId}/candidates`);
      setCandidates((prev) => ({ ...prev, [interviewId]: response.data }));
    } catch (error) {
      toast.error('Failed to load candidates');
    }
  };

  const createInterview = async () => {
    if (!newInterviewName.trim()) {
      toast.error('Interview name is required');
      return;
    }
    try {
      await api.post('/admin/interviews', { name: newInterviewName });
      toast.success('Interview created successfully');
      setNewInterviewName('');
      setShowCreateDialog(false);
      fetchInterviews();
    } catch {
      toast.error('Failed to create interview');
    }
  };

  const toggleActive = async (interviewId, currentStatus) => {
    try {
      await api.put(`/admin/interviews/${interviewId}`, { active: !currentStatus });
      toast.success(`Interview ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchInterviews();
    } catch {
      toast.error('Failed to update interview');
    }
  };

  const deleteInterview = async (interviewId) => {
    if (!window.confirm('Are you sure? This will also delete all associated candidates.')) return;
    try {
      await api.delete(`/admin/interviews/${interviewId}`);
      toast.success('Interview deleted');
      fetchInterviews();
    } catch {
      toast.error('Failed to delete interview');
    }
  };

  const uploadPDF = async (interviewId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/admin/interviews/${interviewId}/upload-pdf`, formData);
      toast.success('PDF uploaded successfully');
      fetchInterviews();
    } catch {
      toast.error('Failed to upload PDF');
    }
  };

  const uploadReportTemplate = async (interviewId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/admin/interviews/${interviewId}/upload-report-template`, formData);
      toast.success('AI report template uploaded');
      fetchInterviews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to upload report template');
    }
  };

  const uploadCSV = async (interviewId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post(`/admin/interviews/${interviewId}/upload-csv`, formData);
      toast.success(`CSV processed: ${response.data.created} created, ${response.data.updated} updated`);
      if (response.data.errors.length > 0) console.error('CSV errors:', response.data.errors);
      fetchCandidates(interviewId);
    } catch {
      toast.error('Failed to upload CSV');
    }
  };

  const downloadExampleCSV = () => {
    const csvContent =
      'name,email,password\nJohn Doe,john@example.com,password123\nJane Smith,jane@example.com,password456\nBob Wilson,bob@example.com,password789';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'example-candidates.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Example CSV downloaded');
  };

  const handleAccordionChange = (value) => {
    if (value && !candidates[value]) fetchCandidates(value);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium">Interview Management</h2>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-interview-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Interview
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Interview</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="interview-name">Interview Name</Label>
                <Input
                  id="interview-name"
                  value={newInterviewName}
                  onChange={(e) => setNewInterviewName(e.target.value)}
                  placeholder="e.g., interview-beasiswa-bca"
                  data-testid="interview-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={createInterview} data-testid="create-interview-submit">
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : interviews.length === 0 ? (
        <p className="text-center text-slate-500 py-12">No interviews created yet</p>
      ) : (
        <Accordion type="single" collapsible onValueChange={handleAccordionChange}>
          {interviews.map((interview) => (
            <AccordionItem key={interview.id} value={interview.id}>
              <div className="flex items-center gap-4 py-2">
                <AccordionTrigger className="flex-1" data-testid={`interview-row-${interview.id}`}>
                  <div className="flex items-center gap-4">
                    <span className="font-medium">{interview.name}</span>
                    {interview.pdf_url && (
                      <FileText className="w-4 h-4 text-blue-600" />
                    )}
                    {interview.report_template_url && (
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    )}
                  </div>
                </AccordionTrigger>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={interview.active}
                    onCheckedChange={() => toggleActive(interview.id, interview.active)}
                    data-testid={`interview-toggle-${interview.id}`}
                  />
                  <span className="text-sm text-slate-500">
                    {interview.active ? 'Active' : 'Inactive'}
                  </span>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteInterview(interview.id)}
                    data-testid={`interview-delete-${interview.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>

              <AccordionContent>
                <div className="space-y-5 pt-4 pl-6 border-l-2 border-slate-200">
                  {/* PDF Guidelines */}
                  <div>
                    <h4 className="text-sm font-medium mb-2">Guidelines PDF</h4>
                    <div className="flex items-center gap-2">
                      <p className={`text-sm ${interview.pdf_url ? 'text-green-600' : 'text-slate-500'}`}>
                        {interview.pdf_url ? 'PDF uploaded' : 'No PDF uploaded'}
                      </p>
                      <label>
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) uploadPDF(interview.id, e.target.files[0]);
                          }}
                        />
                        <Button variant="outline" size="sm" asChild>
                          <span data-testid={`upload-pdf-${interview.id}`}>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload PDF
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>

                  {/* AI Report Template */}
                  <div>
                    <h4 className="text-sm font-medium mb-2">AI Report Template (Excel)</h4>
                    <div className="flex items-center gap-2">
                      <p className={`text-sm ${interview.report_template_url ? 'text-green-600' : 'text-slate-500'}`}>
                        {interview.report_template_url
                          ? 'Template uploaded — AI will use this to generate per-candidate reports'
                          : 'No template uploaded'}
                      </p>
                      <label>
                        <input
                          type="file"
                          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) uploadReportTemplate(interview.id, e.target.files[0]);
                          }}
                        />
                        <Button variant="outline" size="sm" asChild>
                          <span data-testid={`upload-report-template-${interview.id}`}>
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Upload Template
                          </span>
                        </Button>
                      </label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Upload an .xlsx that contains both the report structure (columns like Question / Answer / Score / Notes) and the rubric (criteria with score ranges). Gemini will fill it in for each candidate.
                    </p>
                  </div>

                  {/* Candidates */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-sm font-medium">Candidates</h4>
                      <button
                        onClick={downloadExampleCSV}
                        className="text-xs text-blue-600 hover:text-blue-700 underline flex items-center gap-1"
                        data-testid="download-example-csv"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        example.csv
                      </button>
                    </div>
                    <div className="space-y-2">
                      <label>
                        <input
                          type="file"
                          accept=".csv"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) uploadCSV(interview.id, e.target.files[0]);
                          }}
                        />
                        <Button variant="outline" size="sm" asChild>
                          <span data-testid={`upload-csv-${interview.id}`}>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload CSV
                          </span>
                        </Button>
                      </label>

                      {candidates[interview.id] && candidates[interview.id].length > 0 && (
                        <div className="mt-4">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {candidates[interview.id].map((candidate) => (
                                <TableRow key={candidate.id}>
                                  <TableCell>{candidate.name}</TableCell>
                                  <TableCell>{candidate.email}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};
